import { useState } from 'react'
import type ChallengeBucket from '../../model/ChallengeBucket'
import type { NamedBucketBreakdown } from '../../logic/bucketUtils'
import { getTeamSizeTiers } from '../../logic/teamSizeUtils'
import { useActivities } from '../activity/useActivities'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useLeaderboard } from '../team/useLeaderboard'
import type { LeaderboardTeam } from '../../model/Leaderboard'
import {
  formatRequiredActivityBreakdown,
  getBucketBreakdown,
  type RequiredActivityProgress,
} from '../../util/ActivityIconUtil'

export type MemberFilter = 'all' | 'harder' | number

export interface MemberFilterOption {
  value: MemberFilter
  label: string
}

export interface MilemarkerTeamRow {
  id: string
  name: string
  progress: number
  memberCount: number
  rank: number
  isUserTeam: boolean
  isPaceTeam: boolean
  breakdown: RequiredActivityProgress[]
}

export interface MilemarkerData {
  teams: MilemarkerTeamRow[]
  viewerHasTeam: boolean
  memberFilter: MemberFilter
  setMemberFilter: (filter: MemberFilter) => void
  memberFilterOptions: MemberFilterOption[]
}

const PACE_TEAM_ID = 'pace-team'

function buildPaceTeam(
  paceProgress: number,
  buckets: ChallengeBucket[],
  restDaysRequired: number,
  teamSizeTarget: number,
  challengeGoal: number,
): LeaderboardTeam {
  const countsRestDays = restDaysRequired > 0
  const slots = buckets.length + (countsRestDays ? 1 : 0)
  const pacePerBucket = slots > 0 ? paceProgress / slots : 0
  const avgBucketGoal =
    buckets.length > 0
      ? buckets.reduce((sum, bucket) => sum + bucket.goal, 0) / buckets.length
      : challengeGoal / Math.max(buckets.length, 1)
  const paceRestDayRatio = pacePerBucket / avgBucketGoal
  const paceRestDayCount = countsRestDays
    ? Math.max(0, Math.min(restDaysRequired, paceRestDayRatio * restDaysRequired))
    : 0

  const activityBreakdown = Object.fromEntries(
    buckets.map((bucket) => [bucket.name, pacePerBucket]),
  )

  return {
    id: PACE_TEAM_ID,
    name: 'PACE SETTER',
    progress: paceProgress,
    memberCount: teamSizeTarget,
    activityBreakdown,
    restDayCount: paceRestDayCount,
    restDayTarget: countsRestDays ? restDaysRequired : 0,
  }
}

function toTeamRow(
  team: LeaderboardTeam,
  rank: number,
  currentUserTeamId: string | undefined,
  activities: ReturnType<typeof useActivities>['data'],
  buckets: ChallengeBucket[],
  teamSizeTarget: number,
): MilemarkerTeamRow {
  const isPaceTeam = team.id === PACE_TEAM_ID
  const bucketBreakdown: NamedBucketBreakdown = isPaceTeam
    ? (team.activityBreakdown as NamedBucketBreakdown)
    : getBucketBreakdown(team.activityBreakdown, activities ?? [], buckets)

  return {
    id: team.id,
    name: team.name,
    progress: team.progress,
    memberCount: isPaceTeam ? teamSizeTarget : team.memberCount,
    rank,
    isUserTeam: team.id === currentUserTeamId,
    isPaceTeam,
    breakdown: formatRequiredActivityBreakdown(buckets, bucketBreakdown, {
      count: team.restDayCount,
      target: team.restDayTarget,
    }),
  }
}

function matchesMemberFilter(
  team: MilemarkerTeamRow,
  filter: MemberFilter,
  teamSizeTarget: number,
  teamSizeMin: number,
): boolean {
  if (team.isPaceTeam) return true
  if (filter === 'all') return true
  if (filter === 'harder') {
    return team.memberCount >= teamSizeMin && team.memberCount < teamSizeTarget
  }
  return team.memberCount === filter
}

function buildMemberFilterOptions(
  teamSizeMin: number,
  teamSizeMax: number,
  teamSizeTarget: number,
): MemberFilterOption[] {
  const tiers = getTeamSizeTiers({ teamSizeMin, teamSizeMax, teamSizeTarget })
  const harderTier = tiers.find((tier) => tier.id === 'difficult')
  const options: MemberFilterOption[] = [{ value: 'all', label: 'All teams' }]

  for (let size = teamSizeMax; size > teamSizeTarget; size--) {
    options.push({ value: size, label: String(size) })
  }

  options.push({ value: teamSizeTarget, label: String(teamSizeTarget) })

  if (harderTier && harderTier.sizes.length > 0) {
    options.push({ value: 'harder', label: harderTier.rangeLabel })
  } else {
    for (let size = teamSizeTarget - 1; size >= teamSizeMin; size--) {
      options.push({ value: size, label: String(size) })
    }
  }

  return options
}

export function useMilemarkerData(): {
  data: MilemarkerData | null
  isLoading: boolean
  error: Error | null
} {
  const { data: leaderboard, isLoading, error } = useLeaderboard()
  const { teamID } = useChallengeAccess()
  const { data: challengeContext, isLoading: challengeLoading } = useChallengeContext()
  const { data: activities } = useActivities()
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all')

  if (!leaderboard || !activities || !challengeContext) {
    return { data: null, isLoading: isLoading || challengeLoading, error: error as Error | null }
  }

  const { challenge, buckets, goal, restDaysRequired, teamSizeMin, teamSizeMax, teamSizeTarget } =
    challengeContext
  const paceTeam = buildPaceTeam(
    Math.max(0, challenge.getPaceProgress()),
    buckets,
    restDaysRequired,
    teamSizeTarget,
    goal,
  )
  const teams = [...leaderboard.teams, paceTeam]
    .sort((a, b) => b.progress - a.progress)
    .map((team, index) =>
      toTeamRow(team, index + 1, teamID, activities, buckets, teamSizeTarget),
    )
    .filter((team) => matchesMemberFilter(team, memberFilter, teamSizeTarget, teamSizeMin))

  return {
    data: {
      teams,
      viewerHasTeam: !!teamID,
      memberFilter,
      setMemberFilter,
      memberFilterOptions: buildMemberFilterOptions(teamSizeMin, teamSizeMax, teamSizeTarget),
    },
    isLoading: false,
    error: null,
  }
}
